import React, { Fragment, useRef } from 'react';
import { motion } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import { Users, Mail, Linkedin, Globe, X } from 'lucide-react';

const TeamSection = ({ 
  company, 
  activeTeam, 
  setActiveTeam, 
  selectedMember, 
  setSelectedMember, 
  isBioModalOpen, 
  setIsBioModalOpen 
}) => {
  const teamRef = useRef(null);

  // Helper to handle scroll and set active index
  const handleScroll = (ref, setActive, items) => {
    if (!ref.current) return;
    const scrollLeft = ref.current.scrollLeft;
    const cardWidth = ref.current.offsetWidth * 0.8; // 80% width
    const idx = Math.round(scrollLeft / (cardWidth * 0.95));
    setActive(Math.max(0, Math.min(items - 1, idx)));
  };

  if (!company.teamMembers || company.teamMembers.length === 0) {
    return null;
  }

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-500" /> Our Team
        </h2>
        <div className="relative -mx-6 sm:-mx-10">
          <div
            ref={teamRef}
            className="flex overflow-x-auto gap-6 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-10"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none', 
              scrollPaddingLeft: '24px',
              scrollPaddingRight: '24px',
              WebkitOverflowScrolling: 'touch' 
            }}
            onScroll={() => handleScroll(teamRef, setActiveTeam, company.teamMembers.length)}
          >
            {company.teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg snap-center flex-shrink-0"
                style={{ minWidth: '300px', width: '300px' }}
              >
                <div className="flex items-center gap-4">
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt={member.firstName || member.lastName || member.name || 'Avatar'}
                      className="w-12 h-12 rounded-xl object-cover border-2 border-blue-400 shadow"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl flex items-center justify-center text-white text-lg font-bold">
                      {member.initials || ((member.firstName?.[0] || '') + (member.lastName?.[0] || '')).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 truncate">
                      {member.firstName ? `${member.firstName} ${member.lastName}` : member.name}
                    </h3>
                    {member.role && <p className="text-sm text-gray-600 truncate">{member.role}</p>}
                    <div className="flex gap-2 mt-2">
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="text-gray-400 hover:text-blue-500 transition-colors">
                          <Mail className="w-4 h-4" />
                        </a>
                      )}
                      {member.linkedin && (
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 transition-colors">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {member.website && (
                        <a href={member.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 transition-colors">
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                {member.bio && (
                  <div className="mt-4">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {member.bio.split(' ').length > 50 
                        ? `${member.bio.split(' ').slice(0, 50).join(' ')}...`
                        : member.bio}
                    </p>
                    {member.bio.split(' ').length > 50 && (
                      <button
                        onClick={() => {
                          setSelectedMember(member);
                          setIsBioModalOpen(true);
                        }}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        See more
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Live Dot pagination for team */}
          <div className="flex justify-center mt-4 gap-2">
            {company.teamMembers.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setActiveTeam(idx);
                  if (teamRef.current) {
                    const cardWidth = 300 + 24; // card width + gap
                    teamRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                  }
                }}
                className={`w-2 h-2 rounded-full inline-block transition-all duration-200 focus:outline-none ${activeTeam === idx ? 'bg-blue-500 scale-125' : 'bg-gray-300'}`}
                aria-label={`Show team member ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Bio Modal */}
      <Transition appear show={isBioModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsBioModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {selectedMember?.firstName ? `${selectedMember.firstName} ${selectedMember.lastName}` : selectedMember?.name}
                    </Dialog.Title>
                    <button
                      onClick={() => setIsBioModalOpen(false)}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  {selectedMember?.role && (
                    <p className="text-sm text-gray-600 mb-4">{selectedMember.role}</p>
                  )}
                  <div className="mt-2">
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                      {selectedMember?.bio}
                    </p>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                      onClick={() => setIsBioModalOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default TeamSection; 